import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import {
  LayoutDashboard, ShoppingBag, Tags, Shirt, Scissors, Grid3x3, Sparkles,
  FileText, Package, Images, Settings as SettingsIcon, Inbox, LogOut, Menu, X,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: ShoppingBag },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/styles", label: "Style Library", icon: Shirt },
  { to: "/admin/fabrics", label: "Fabrics", icon: Scissors },
  { to: "/admin/compatibility", label: "Design ↔ Fabric", icon: Grid3x3 },
  { to: "/admin/addons", label: "Add-Ons", icon: Sparkles },
  { to: "/admin/requests", label: "Custom Requests", icon: FileText },
  { to: "/admin/orders", label: "Orders", icon: Package },
  { to: "/admin/gallery", label: "Gallery", icon: Images },
  { to: "/admin/content", label: "Content & Settings", icon: SettingsIcon },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox },
];

export default function AdminLayout() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Session still verifying OR backend waking up → show the admin loader,
  // never fall through to a customer-facing page.
  if (!checked || user === null) {
    return (
      <div data-testid="admin-loading" className="flex min-h-screen flex-col items-center justify-center gap-4 bg-kys-ink px-6 text-center">
        <span className="font-serif text-3xl text-white">Kys Boutique</span>
        <div className="flex items-center gap-2 text-white/70">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          <span className="text-sm">Verifying your session…</span>
        </div>
      </div>
    );
  }
  // Only reached on a genuine expired/invalid session.
  if (user === false) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const SidebarContent = () => (
    <>
      <div className="px-5 py-5">
        <NavLink to="/" className="font-serif text-2xl text-white">Kys Boutique</NavLink>
        <p className="mt-0.5 text-[11px] text-white/40">Owner Dashboard</p>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 pb-4">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 rounded px-3 py-2.5 text-sm font-medium transition ${isActive ? "bg-kys-accent text-white" : "text-white/70 hover:bg-white/10"}`}>
            <n.icon className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <button data-testid="admin-logout" onClick={doLogout} className="m-3 flex items-center gap-2 rounded px-3 py-2.5 text-sm font-medium text-white/70 hover:bg-white/10">
        <LogOut className="h-4 w-4" /> Log out
      </button>
    </>
  );

  return (
    <div className="flex min-h-screen bg-kys-cream">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col bg-kys-ink lg:flex">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative flex w-64 flex-col bg-kys-ink">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 text-white/60"><X className="h-5 w-5" /></button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-kys-blush-border bg-white px-4 lg:hidden">
          <button onClick={() => setOpen(true)}><Menu className="h-6 w-6" /></button>
          <span className="font-serif text-xl">Kys Boutique</span>
        </header>
        <main className="flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
